// src/sim/controllers/local-input-controller.ts — D-10 local input + VIS-03 torch.
//
// Phase 1 wired nipplejs into the setIntent() buffer. Phase 2 Plan 04 adds a
// one-shot requestTorchToggle() for the TorchButton UI (D-19..D-22):
//   - requestTorchToggle() arms a pending flag
//   - the next decide() returns intent.actions.toggleTorch = true once, then
//     auto-clears (consume-then-clear in a single call)
//   - subsequent decide() calls return toggleTorch = false until another request
//   - reset() clears both the pending flag and the latest intent
//
// Phase 3 Plan 01 adds one-shot requestToggleHide() + requestDropToFloor() + a
// HELD peek channel via setPeekHeld(bool). Peek is intentionally NOT one-shot —
// the UI owns the down/up edges and mirrors the finger state to sim.
//
// Contract rationale: multi-tap within one sim tick collapses to a single
// toggle (T-02-12 mitigation). A bool flag is sufficient because duplicate
// requests inside the same tick are indistinguishable by design — the user
// can only meaningfully change their mind between ticks.
//
// Source: Phase 2 Plan 04 Task 1; Phase 3 Plan 01 Task 4;
// 03-CONTEXT.md D-21 / D-25; 03-PATTERNS.md §local-input-controller.ts EXTEND.

import type { CharacterController } from './controller';
import type { Intent } from '../../shared/types/intent';
import type { Perception } from '../../shared/types/perception';
import { ZERO_INTENT } from '../../shared/types/intent';

/**
 * P0 LocalInput stub (D-10) extended in P2 for VIS-03 and P3 for hide/peek.
 *
 * latestIntent: last movement / facing buffer pushed by the joystick.
 * pendingTorchToggle: one-shot flag set by TorchButton, consumed on decide().
 * pendingToggleHide: one-shot (P3 D-21) — tap to enter/exit a Tiled hiding spot.
 * pendingDropToFloor: one-shot (P3 D-25) — dedicated button for on-floor ability.
 * peekHeld: HELD (P3 D-21) — mirrors the long-press finger state; NOT one-shot.
 */
export class LocalInputController implements CharacterController {
  private latestIntent: Intent = ZERO_INTENT;
  private pendingTorchToggle = false;
  private pendingToggleHide = false;   // P3 D-21 one-shot
  private pendingDropToFloor = false;  // P3 D-25 one-shot
  private peekHeld = false;            // P3 D-21 HELD state (not one-shot)

  /** Called by the UI layer (Joystick) to push the latest joystick/touch state. */
  setIntent(intent: Intent): void {
    this.latestIntent = intent;
  }

  /**
   * Called by src/ui/controls/TorchButton.tsx on pointerdown. One-shot: the
   * next decide() sees toggleTorch=true; subsequent decide() calls see false
   * until another request.
   */
  requestTorchToggle(): void {
    this.pendingTorchToggle = true;
  }

  /**
   * P3 D-21 — called by HideExitButton on short-tap. One-shot: next decide()
   * sees actions.toggleHide=true, subsequent calls see false until another request.
   */
  requestToggleHide(): void {
    this.pendingToggleHide = true;
  }

  /**
   * P3 D-25 — called by OnFloorDropButton on tap. One-shot: next decide()
   * sees actions.dropToFloor=true, subsequent calls see false until another request.
   */
  requestDropToFloor(): void {
    this.pendingDropToFloor = true;
  }

  /**
   * P3 D-21 — called by HideExitButton on long-press down/up. HELD state:
   * while true, every decide() reports actions.peek=true until the UI
   * calls setPeekHeld(false). The UI owns the edges; sim mirrors the finger.
   */
  setPeekHeld(held: boolean): void {
    this.peekHeld = held;
  }

  decide(_perception: Perception, _dt: number): Intent {
    const torch = this.pendingTorchToggle;
    const hide = this.pendingToggleHide;
    const drop = this.pendingDropToFloor;
    const peek = this.peekHeld;
    this.pendingTorchToggle = false;
    this.pendingToggleHide = false;
    this.pendingDropToFloor = false;
    // peekHeld is NOT cleared — it mirrors the UI finger-down state until
    // the UI clears it via setPeekHeld(false).

    if (!torch && !hide && !drop && !peek) return this.latestIntent;
    return {
      ...this.latestIntent,
      actions: {
        ...this.latestIntent.actions,
        toggleTorch: torch,
        toggleHide: hide,
        dropToFloor: drop,
        peek,
      },
    };
  }

  reset(): void {
    this.latestIntent = ZERO_INTENT;
    this.pendingTorchToggle = false;
    this.pendingToggleHide = false;
    this.pendingDropToFloor = false;
    this.peekHeld = false;
  }
}
